import type { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2, Paperclip } from 'lucide-react';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { RowActionList, RowViewLink, type RowAction } from '@/components/common/RowActions';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EMPTY_VALUE } from '@/constants/app.constants';
import type { PromoDrillParams } from '@/features/promotion/types/promotion.types';
import { APPROVAL_STATUS_TONE } from '@/styles/statusColors';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import {
  DECISION_LABEL,
  DECISION_TONE,
  DONATION_DECISION,
} from '../constants/marketPromotion.constants';
import type {
  MpAdjustmentRow,
  MpApprovalRow,
  MpRegionRow,
  MpSummaryRow,
} from '../types/marketPromotion.types';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const taka = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

function decisionBadge(approveTypeNo: number) {
  return (
    <StatusBadge tone={DECISION_TONE[approveTypeNo] ?? APPROVAL_STATUS_TONE.pending}>
      {DECISION_LABEL[approveTypeNo] ?? DECISION_LABEL[DONATION_DECISION.PENDING]}
    </StatusBadge>
  );
}

export function getMpAdjustmentColumns(
  zmSearch: PromoDrillParams,
): ColumnDef<MpAdjustmentRow, unknown>[] {
  return [
    createSerialColumn<MpAdjustmentRow>(),
    {
      accessorKey: 'name',
      header: 'Promotion',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">{row.original.name}</WrappingCellText>
      ),
    },
    {
      accessorKey: 'itemNo',
      header: 'Item Id / Item Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.itemNo}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.itemCode}
        />
      ),
    },
    {
      accessorKey: 'distributedAmount',
      header: 'Distributed (Tk)',
      cell: ({ row }) => taka(row.original.distributedAmount),
    },
    createActionsColumn<MpAdjustmentRow>((item) => (
      <RowViewLink
        to="/market-promotion/adjustment/$promoItemId"
        params={{ promoItemId: item.promoItemId }}
        search={zmSearch}
        label="View ZMs"
        aria-label={`ZMs who handed out ${item.name}`}
      />
    )),
  ];
}

export function getMpSummaryColumns(): ColumnDef<MpSummaryRow, unknown>[] {
  return [
    createSerialColumn<MpSummaryRow>(),
    { accessorKey: 'name', header: 'Budget Holder', cell: ({ row }) => row.original.name },
    { accessorKey: 'hrEmpId', header: 'HR ID', cell: ({ row }) => dash(row.original.hrEmpId) },
    {
      accessorKey: 'designationName',
      header: 'Designation',
      cell: ({ row }) => dash(row.original.designationName),
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
      cell: ({ row }) => dash(row.original.departmentName),
    },
    {
      accessorKey: 'divisionName',
      header: 'Division',
      cell: ({ row }) => dash(row.original.divisionName),
    },
    {
      accessorKey: 'budgetAmount',
      header: 'Budget (Tk)',
      cell: ({ row }) => taka(row.original.budgetAmount),
    },
    {
      accessorKey: 'disburseAmount',
      header: 'Disburse (Tk)',
      cell: ({ row }) => taka(row.original.disburseAmount),
    },
    {
      accessorKey: 'approvedAmount',
      header: 'Approved (Tk)',
      cell: ({ row }) => taka(row.original.approvedAmount),
    },
    {
      accessorKey: 'duesAmount',
      header: 'Dues (Tk)',
      cell: ({ row }) => (
        <span className={row.original.duesAmount < 0 ? 'text-destructive' : undefined}>
          {taka(row.original.duesAmount)}
        </span>
      ),
    },
  ];
}

export function getMpRegionColumns(): ColumnDef<MpRegionRow, unknown>[] {
  return [
    createSerialColumn<MpRegionRow>(),
    {
      accessorKey: 'regionName',
      header: 'Region / Side',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.regionName}
          primaryClassName="font-medium"
          secondary={row.original.sideRegion}
        />
      ),
    },
    { accessorKey: 'rsmName', header: 'RSM', cell: ({ row }) => row.original.rsmName },
    { accessorKey: 'smName', header: 'SM', cell: ({ row }) => dash(row.original.smName) },
    {
      accessorKey: 'distributedAmount',
      header: 'Distributed (Tk)',
      cell: ({ row }) => taka(row.original.distributedAmount),
    },
  ];
}

interface ApprovalActions {
  onRecommend?: (row: MpApprovalRow) => void;
  onDecide?: (row: MpApprovalRow) => void;
  onView: (row: MpApprovalRow) => void;
  onAmend?: (row: MpApprovalRow) => void;
  onCall?: (row: MpApprovalRow) => void;
}

export function getMpApprovalColumns(
  actions: ApprovalActions,
  showCall: boolean,
): ColumnDef<MpApprovalRow, unknown>[] {
  const columns: ColumnDef<MpApprovalRow, unknown>[] = [
    createSerialColumn<MpApprovalRow>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          {dash(row.original.instituteName)}
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'teacherMobile',
      header: 'Teacher',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.teacherName)}
          secondary={row.original.teacherMobile}
        />
      ),
    },
    { accessorKey: 'itemName', header: 'Promotion', cell: ({ row }) => row.original.itemName },
    {
      accessorKey: 'dcrDate',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.dcrDate),
    },
    {
      accessorKey: 'enteredByName',
      header: 'Given By',
      cell: ({ row }) => dash(row.original.enteredByName),
    },
    {
      accessorKey: 'claimedAmount',
      header: 'Claimed (Tk)',
      cell: ({ row }) => taka(row.original.claimedAmount),
    },
    {
      accessorKey: 'approvedAmount',
      header: 'Approved (Tk)',
      cell: ({ row }) => taka(row.original.approvedAmount),
    },
    {
      id: 'attachment',
      header: 'File',
      cell: ({ row }) =>
        row.original.hasAttachment ? (
          <Paperclip className="size-4 text-muted-foreground" aria-label="Has paperwork" />
        ) : (
          '—'
        ),
    },
    {
      accessorKey: 'approveTypeNo',
      header: 'Status',
      cell: ({ row }) => decisionBadge(row.original.approveTypeNo),
    },
  ];

  if (showCall) {
    columns.push(
      {
        accessorKey: 'calledBy',
        header: 'Called By',
        cell: ({ row }) => dash(row.original.calledBy),
      },
      {
        accessorKey: 'isMisVerified',
        header: 'MIS',
        cell: ({ row }) =>
          row.original.isMisVerified ? (
            <CheckCircle2 className="size-4 text-emerald-600" aria-label="Verified" />
          ) : (
            '—'
          ),
      },
    );
  }

  columns.push(
    createActionsColumn<MpApprovalRow>((approval) => (
      <RowActionList
        actions={getApprovalRowActions(approval, actions, showCall)}
        moreActionsAriaLabel={`More actions for ${approval.instituteName ?? 'this promotion'}`}
      />
    )),
  );

  return columns;
}

function getApprovalRowActions(
  approval: MpApprovalRow,
  actions: ApprovalActions,
  showCall: boolean,
): RowAction[] {
  const { onRecommend, onDecide, onAmend, onCall } = actions;
  const instituteName = approval.instituteName ?? 'this promotion';
  const rowActions: RowAction[] = [];

  if (approval.approveTypeNo === DONATION_DECISION.PENDING && onRecommend) {
    rowActions.push({
      key: 'recommend',
      label: 'Recommend',
      ariaLabel: `Recommend the promotion for ${instituteName}`,
      onSelect: () => onRecommend(approval),
    });
  }
  if (approval.approveTypeNo === DONATION_DECISION.RECOMMENDED && onDecide) {
    rowActions.push({
      key: 'approve',
      label: 'Approve',
      ariaLabel: `Approve the promotion for ${instituteName}`,
      onSelect: () => onDecide(approval),
    });
  }
  rowActions.push({
    key: 'view',
    label: 'View',
    ariaLabel: `View the promotion for ${instituteName}`,
    onSelect: () => actions.onView(approval),
  });
  if (onAmend) {
    rowActions.push({
      key: 'edit',
      label: 'Edit',
      ariaLabel: `Edit the promotion for ${instituteName}`,
      onSelect: () => onAmend(approval),
    });
  }
  if (showCall && onCall) {
    rowActions.push({
      key: 'call',
      label: 'Call',
      ariaLabel: `Call about the promotion for ${instituteName}`,
      onSelect: () => onCall(approval),
    });
  }
  return rowActions;
}
